// Workspace Persistence Manager
// Architecture: v4.0.0 Multiple Charts Implementation
// Auto-save and session restoration for workspace state

import { devError } from '../../../utils/devLog';
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useMultipleCharts } from './MultipleChartsContext';
import { WorkspaceState, ChartInstance } from '../../../types/MultipleCharts';
import { getStorageService } from '../../../services/StorageService';

/**
 * Auto-save Configuration
 */
export interface AutoSaveConfig {
  enabled: boolean;
  intervalMs: number;          // Auto-save interval in milliseconds
  maxBackups: number;          // Maximum number of backup workspaces to keep
  saveOnTabSwitch: boolean;    // Save when switching tabs
  saveOnChartLoad: boolean;    // Save when loading new charts
  saveOnChartClose: boolean;   // Save when closing charts
  debounceMs: number;          // Debounce time for rapid changes
  debugMode?: boolean;         // Enable debug logging
  suppressAnimationsOnRestore?: boolean;  // Suppress chart animations during workspace restoration
}

/**
 * Default auto-save configuration
 */
const DEFAULT_AUTO_SAVE_CONFIG: AutoSaveConfig = {
  enabled: true,
  intervalMs: 30000,           // 30 seconds
  maxBackups: 5,
  saveOnTabSwitch: true,
  saveOnChartLoad: true,
  saveOnChartClose: true,
  debounceMs: 2000,            // 2 seconds debounce
  debugMode: process.env.NODE_ENV === 'development',
  suppressAnimationsOnRestore: true  // Suppress animations during page reload restore
};

/**
 * Session Storage Keys
 */
const SESSION_KEYS = {
  WORKSPACE: 'hora_workspace_session',
  LAST_SAVE: 'hora_last_save_timestamp',
  AUTO_SAVE_CONFIG: 'hora_auto_save_config'
} as const;

/**
 * Workspace Persistence Hook
 * Manages auto-save and session restoration
 */
export function useWorkspacePersistence(config: Partial<AutoSaveConfig> = {}) {
  const {
    workspace,
    charts,
    activeChartId,
    loadedChartIds,
    saveWorkspace,
    loadWorkspace
  } = useMultipleCharts();
  
  const [autoSaveConfig, setAutoSaveConfig] = useState<AutoSaveConfig>({
    ...DEFAULT_AUTO_SAVE_CONFIG,
    ...config
  });
  
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);
  
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const restorationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastWorkspaceStateRef = useRef<string>('');
  
  const toast = useToast();

  /**
   * Create workspace snapshot for persistence
   */
  const createWorkspaceSnapshot = useCallback((): any => {
    return {
      workspace,
      charts: Array.from(charts.entries()).map(([id, chart]) => [id, {
        ...chart,
        // Serialize dates
        lastAccessed: chart.lastAccessed.toISOString(),
        metadata: {
          ...chart.metadata,
          created: chart.metadata.created.toISOString(),
          modified: chart.metadata.modified.toISOString(),
          accessed: chart.metadata.accessed.toISOString()
        }
      }]),
      activeChartId,
      loadedChartIds,
      timestamp: new Date().toISOString()
    };
  }, [workspace, charts, activeChartId, loadedChartIds]);

  /**
   * Save workspace to session storage
   */
  const saveToSession = useCallback(async (force: boolean = false): Promise<void> => {
    if (!autoSaveConfig.enabled && !force) return;
    
    try {
      const snapshot = createWorkspaceSnapshot();
      const snapshotString = JSON.stringify(snapshot);
      
      // Check if workspace state has changed
      if (!force && snapshotString === lastWorkspaceStateRef.current) {
        return; // No changes to save
      }
      
      setIsAutoSaving(true);
      
      // Save to session storage
      sessionStorage.setItem(SESSION_KEYS.WORKSPACE, snapshotString);
      sessionStorage.setItem(SESSION_KEYS.LAST_SAVE, new Date().toISOString());
      
      // Save to persistent storage (IndexedDB)
      await saveWorkspace();
      
      // Update state
      lastWorkspaceStateRef.current = snapshotString;
      setLastSaveTime(new Date());
      setPendingSave(false);
      
      if (autoSaveConfig.debugMode) {
      }
      
    } catch (error) {
      devError('Failed to save workspace:', error);
      toast({
        title: 'Auto-save Failed',
        description: 'Failed to save workspace automatically',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setIsAutoSaving(false);
    }
  }, [autoSaveConfig, createWorkspaceSnapshot, saveWorkspace, charts.size, activeChartId, toast]);

  /**
   * Restore workspace from session storage
   */
  const restoreFromSession = useCallback(async (): Promise<boolean> => {
    try {
      const savedWorkspace = sessionStorage.getItem(SESSION_KEYS.WORKSPACE);
      const lastSaveTimestamp = sessionStorage.getItem(SESSION_KEYS.LAST_SAVE);
      
      if (!savedWorkspace) {
        return false; // No session to restore
      }
      
      const snapshot = JSON.parse(savedWorkspace);
      const saveTime = lastSaveTimestamp ? new Date(lastSaveTimestamp) : null;
      
      // Check if session is recent enough (within 24 hours)
      if (saveTime) {
        const hoursSinceLastSave = (Date.now() - saveTime.getTime()) / (1000 * 60 * 60);
        if (hoursSinceLastSave > 24) {
          return false;
        }
      }
      
      // Set animation suppression flag for chart renderers during restore
      if (autoSaveConfig.suppressAnimationsOnRestore) {
        // Store restoration flag in global context for chart renderers
        if (typeof window !== 'undefined') {
          window.isWorkspaceRestoring = true;

          // Clear any previous restoration timer
          if (restorationTimerRef.current) {
            clearTimeout(restorationTimerRef.current);
          }

          // Clear flag after restoration completes (5 second delay for chart loading)
          restorationTimerRef.current = setTimeout(() => {
            window.isWorkspaceRestoring = false;
            restorationTimerRef.current = null;
          }, 5000);
        }
      }
      
      // Try persistent storage first
      await loadWorkspace();
      
      // If persistent storage was empty but session has charts, use session data
      if (snapshot.charts && Array.isArray(snapshot.charts) && snapshot.charts.length > 0) {
        // Check if loadWorkspace actually loaded any charts
        const currentChartsCount = charts.size;
        
        if (currentChartsCount === 0) {
          // IndexedDB was empty, use session storage data
          
          // Deserialize charts from session snapshot
          const chartMap = new Map();
          snapshot.charts.forEach(([id, chart]: [string, any]) => {
            chartMap.set(id, {
              ...chart,
              // Deserialize dates
              lastAccessed: new Date(chart.lastAccessed),
              metadata: {
                ...chart.metadata,
                created: new Date(chart.metadata.created),
                modified: new Date(chart.metadata.modified),
                accessed: new Date(chart.metadata.accessed)
              }
            });
          });

        } else {
          // Charts were loaded from IndexedDB successfully
          toast({
            title: 'Session Restored',
            description: `Restored workspace with ${snapshot.charts.length} charts`,
            status: 'success',
            duration: 3000,
            isClosable: true
          });
        }
      }
      
      setLastSaveTime(saveTime);
      
      return true;
      
    } catch (error) {
      devError('Failed to restore workspace session:', error);
      // Clear corrupted session data
      sessionStorage.removeItem(SESSION_KEYS.WORKSPACE);
      sessionStorage.removeItem(SESSION_KEYS.LAST_SAVE);
      return false;
    }
  }, [loadWorkspace, toast, autoSaveConfig.suppressAnimationsOnRestore, autoSaveConfig.debugMode, charts.size]);

  /**
   * Debounced save function
   */
  const debouncedSave = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    setPendingSave(true);
    
    debounceTimerRef.current = setTimeout(() => {
      saveToSession();
    }, autoSaveConfig.debounceMs);
  }, [saveToSession, autoSaveConfig.debounceMs]);

  /**
   * Trigger save based on configuration
   */
  const triggerSave = useCallback((reason: string) => {
    if (!autoSaveConfig.enabled) return;
    
    switch (reason) {
      case 'tab-switch':
        if (autoSaveConfig.saveOnTabSwitch) debouncedSave();
        break;
      case 'chart-load':
        if (autoSaveConfig.saveOnChartLoad) debouncedSave();
        break;
      case 'chart-close':
        if (autoSaveConfig.saveOnChartClose) debouncedSave();
        break;
      case 'manual':
      case 'interval':
        saveToSession();
        break;
      default:
        debouncedSave();
    }
  }, [autoSaveConfig, debouncedSave, saveToSession]);

  // Set up auto-save interval
  useEffect(() => {
    if (!autoSaveConfig.enabled) return;
    
    if (autoSaveTimerRef.current) {
      clearInterval(autoSaveTimerRef.current);
    }
    
    autoSaveTimerRef.current = setInterval(() => {
      triggerSave('interval');
    }, autoSaveConfig.intervalMs);
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, [autoSaveConfig.enabled, autoSaveConfig.intervalMs, triggerSave]);

  // Monitor workspace changes
  useEffect(() => {
    triggerSave('workspace-change');
  }, [workspace, charts.size, activeChartId, loadedChartIds.length, triggerSave]);

  // Save on window unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (pendingSave || autoSaveConfig.enabled) {
        // Force immediate save before unload
        const snapshot = createWorkspaceSnapshot();
        sessionStorage.setItem(SESSION_KEYS.WORKSPACE, JSON.stringify(snapshot));
        sessionStorage.setItem(SESSION_KEYS.LAST_SAVE, new Date().toISOString());
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [pendingSave, autoSaveConfig.enabled, createWorkspaceSnapshot]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (restorationTimerRef.current) {
        clearTimeout(restorationTimerRef.current);
      }
    };
  }, []);

  return {
    // State
    autoSaveConfig,
    lastSaveTime,
    isAutoSaving,
    pendingSave,
    
    // Actions
    saveToSession: () => saveToSession(true),
    restoreFromSession,
    triggerSave,
    updateConfig: setAutoSaveConfig,
    
    // Utils
    hasUnsavedChanges: pendingSave
  };
}

/**
 * Workspace Persistence Provider Component
 * Wraps the application to provide auto-save functionality
 */
export interface WorkspacePersistenceProviderProps {
  children: React.ReactNode;
  config?: Partial<AutoSaveConfig>;
  enableSessionRestore?: boolean;
}

export const WorkspacePersistenceProvider: React.FC<WorkspacePersistenceProviderProps> = ({
  children,
  config,
  enableSessionRestore = true
}) => {
  const {
    restoreFromSession,
    autoSaveConfig,
    lastSaveTime,
    isAutoSaving
  } = useWorkspacePersistence(config);
  
  const [hasAttemptedRestore, setHasAttemptedRestore] = useState(false);
  
  // Используем ref для актуальной функции восстановления
  const restoreRef = useRef(restoreFromSession);
  restoreRef.current = restoreFromSession;

  // Attempt session restore on mount
  useEffect(() => {
    if (!enableSessionRestore || hasAttemptedRestore) return;

    const attemptRestore = async () => {
      try {
        const restored = await restoreRef.current();
        if (restored) {
          // Session restored successfully
        }
      } catch (error) {
        devError('Failed to restore session:', error);
      } finally {
        setHasAttemptedRestore(true);
      }
    };

    // Delay restore to allow context initialization
    const restoreTimer = setTimeout(attemptRestore, 1000);

    return () => clearTimeout(restoreTimer);
  }, [enableSessionRestore, hasAttemptedRestore]);
  
  // Expose persistence info to window for debugging
  useEffect(() => {
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      (window as any).workspacePersistence = {
        config: autoSaveConfig,
        lastSaveTime,
        isAutoSaving,
        restoreFromSession
      };
    }
  }, [autoSaveConfig, lastSaveTime, isAutoSaving, restoreFromSession]);
  
  return <>{children}</>;
};

export default {
  useWorkspacePersistence,
  WorkspacePersistenceProvider
};